import {useMemo,useState} from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import PersonIcon from "@mui/icons-material/Person";
import { NavLink, Outlet } from "react-router-dom";
import Typography from "@mui/material/Typography";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MyCambridgeLogo from "../assests/MyCambridgeLogo.png";
import ListAltIcon from "@mui/icons-material/ListAlt";
import BarChartIcon from "@mui/icons-material/BarChart";
import ReplayIcon from "@mui/icons-material/Replay";
import UserProfileMenu from "./UserProfileMenu";
import { useAuthUser } from "../Hooks/useAuthUser";
import SummarizeIcon from '@mui/icons-material/Summarize';
import "./Style.css";
import SettingsIcon from "@mui/icons-material/Settings";
import PropTypes from "prop-types";
const drawerWidth = 240;

// Generic: get allowed roles for Settings from env or config
const settingsRolesEnv = process.env.REACT_APP_SETTINGS_ROLES;
let allowedSettingsRoles = [];
if (settingsRolesEnv) {
  try {
    allowedSettingsRoles = JSON.parse(settingsRolesEnv);
    if (!Array.isArray(allowedSettingsRoles)) allowedSettingsRoles = [];
  } catch {
    allowedSettingsRoles = [];
  }
} else {
  // fallback to previous default if env not set
  allowedSettingsRoles = [
    "CuckooDev-Group-Security-Admin",
    "CuckooDev-ETO-Engineer",
  ];
}

const  NavBar = (props) =>{
  const { window } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, groups } = useAuthUser();
  // Compute once whether Settings should be shown
  const showSettings = useMemo(
    () => allowedSettingsRoles.some((role) => groups?.includes(role)),
    [allowedSettingsRoles, groups]
  );
  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const iconMap = {
      Dashboard: <DashboardIcon />,
      "Candidate Upload": <PersonIcon />,
      "Candidate List": <ListAltIcon />,
      "Candidate Reports": <BarChartIcon />,
      "Re-review Process": <ReplayIcon />,
      "Audit Log": <SummarizeIcon />,
      Settings: <SettingsIcon />,
    };
const navItems = [
  { text: "Dashboard", path: "/v0/dashboard" },
  { text: "Candidate Upload", path: "/v0/candidate" },
  { text: "Candidate List", path: "/v0/candidatelist" },
  { text: "Candidate Reports", path: "/v0/reports" },
  { text: "Re-review Process", path: "/v0/rereview" },
  // Conditionally render Audit Log for allowed roles
  ...(showSettings ? [{ text: "Audit Log", path: "/v0/auditreport" }] : []),
  // Settings will be conditionally added below
];
  const drawer = (
    <div>
      <Box>
        <img src={MyCambridgeLogo} alt="My Cambridge Logo" />
      </Box>
      <Divider />
      <Divider />
      <List>
        {navItems.map(({ text, path }) => (
          <ListItem key={text} disablePadding onClick={handleDrawerClose}>
            <ListItemButton
              component={NavLink}
              to={path}
              sx={(theme) => ({
                color: "inherit",
                textDecoration: "none",
                "&.active": {
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  borderRadius: 1,
                },
              })}
            >
              <ListItemIcon sx={{ color: "inherit" }}>
                {iconMap[text]}
              </ListItemIcon>
              <ListItemText primary={text} />
            </ListItemButton>
          </ListItem>
        ))}
        {/* Conditionally render Settings menu item */}
        {showSettings && (
          <ListItem key="Settings " disablePadding onClick={handleDrawerClose}>
            <ListItemButton
              component={NavLink}
              to="/v0/setting"
              sx={(theme) => ({
                color: "inherit",
                textDecoration: "none",
                "&.active": {
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  borderRadius: 1,
                },
              })}
            >
              <ListItemIcon sx={{ color: "inherit" }}>
                {iconMap["Settings"]}
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </div>
  );
  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box className="navbar__container">
     <CssBaseline />
      <AppBar 
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{justifyContent:"space-between"}}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
 sx={{ mr: 2, display: { sm: 'none' } }}          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Candidate Imposter Application
          </Typography>
          <UserProfileMenu userInfo={user} />
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          slotProps={{
            root: {
              keepMounted: true, // Better open performance on mobile.
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#f8f9fa",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: "12px",
          width: !mobileOpen
            ? "100%" // full width when mobile drawer is open
      : { sm: `calc(100% - ${drawerWidth}px)` }, // apply only from small screens upward
  }}
      >
        <Toolbar /> 
      
        <Outlet />
      </Box>
    </Box>
  );
}
NavBar.propTypes = {
  window: PropTypes.func,
};

export default NavBar;
